import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { useRecoilValue, useRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  DatePicker,
  Tabs,
  Table,
  Spin,
  Tag
} from 'antd';
import Button from '../../shared-components/buttons/Button';
import MainPage from '../../shared-components/main-page/MainPage';
import { H1 } from '../../shared-components/typography/Title';
import { userPrivilagesAtom, notificationsAtom, reloadSpecialServicesAtom } from '../../../atoms/Atoms';
import api from '../../../api/api';
import { localizeSpecialServiceDates, localizeParatransitServiceDates, thousand, specialServiceStatusToString, specialServiceStatusToColor, paratransitServiceStatusToColor, paratransitServiceStatusToString } from '../../../functions';
import Select from '../../shared-components/form/Select';
import SelectedSpecialServicesModal from './shared-components/SelectedSpecialServicesModal';
import SelectedParatransitServicesModal from './shared-components/SelectedParatransitServicesModal';
import envConfig from '../../../envConfig';

function TransdevSpecialServices() {
  const defaultSpecialService = {
    date: dayjs().format('MMM DD, YYYY hh:mm A'),
    controlNumber: '',
    requestDescription: '',
    startDate: null,
    endDate: null,
    busNumber: '',
    unitNumber: '',
    realizedDescription: '',
    actEmployeeName: '',
    actEmployeeNumber: '',
    actEmployeeSignedDate: null,
    firstTransitEmployeeName: '',
    firstTransitEmployeeSignedDate: null,
    fileData: null,
    status: 0
  };
  const defaultParatransitService = {
    date: dayjs().format('MMM DD, YYYY'),
    driverName: '',
    unitBusNumber: '',
    supervisorName: '',
    garageDepartureTime: null,
    garageArrivalTime: null,
    initialMileage: null,
    finalMileage: null,
    routes: [],
    status: 0,
    passengers: null
  };
  const { t, i18n } = useTranslation();
  const { RangePicker } = DatePicker;
  const userPrivilages = useRecoilValue(userPrivilagesAtom);
  const [reloadSpecialServices, setReloadSpecialServices] = useRecoilState(reloadSpecialServicesAtom);
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [selectedDates, setSelectedDates] = useState(null);
  const [selectedTab, setSelectedTab] = useState('special');
  const [selectedSpecialService, setSelectedSpecialService] = useState(null);
  const [selectedParatransitService, setSelectedParatransitService] = useState(null);
  const [special, setSpecial] = useState({
    loading: false,
    initialLoad: true,
    page: 0,
    size: 20,
    totalCount: 0,
    filter: 0,
    data: []
  });
  const [paratransit, setParatransit] = useState({
    loading: false,
    initialLoad: true,
    page: 0,
    size: 20,
    totalCount: 0,
    filter: 0,
    data: []
  });

  function getSpecialData(page) {
    if (!selectedDates?.start || !selectedDates?.end) {
      return;
    }
    setSpecial({
      ...special,
      page,
      loading: true,
      initialLoad: false
    });
    const startDate = dayjs(selectedDates.start);
    const endDate = dayjs(selectedDates.end);
    const startString = startDate.add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss');
    const endString = endDate.add(4, 'hour').add(1, 'day').add(-1, 'second').format('YYYY-MM-DDTHH:mm:ss');
    api.get(`transdev/specialservice?page=${page}&size=${special.size}&startDate=${startString}&endDate=${endString}&filter=${special?.filter}`)
    .then((response) => {
      if (response.data.success) {
        setSpecial({
          ...special,
          loading: false,
          totalCount: response.data.data.total,
          data: _.map(response.data.data.services, (s) => localizeSpecialServiceDates(s)),
          draftCount: response.data.data.draftCount,
          approvedCount: response.data.data.approvedCount,
          preApprovedCount: response.data.data.preApprovedCount,
          submittedCount: response.data.data.submittedCount,
          rejectedCount: response.data.data.rejectedCount
        });
      } else {
        setSpecial({
          ...special,
          loading: false
        });
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: response.data.error,
            error: true,
          }
        ]);
      }
    })
    .catch((error) => {
      setSpecial({
        ...special,
        loading: false
      });
      setNotifications([
        ...notifications,
        {
          title: t('error'),
          description: error.message,
          error: true,
        }
      ]);
    });
  }

  function getParatransitData(page) {
    if (!selectedDates?.start || !selectedDates?.end) {
      return;
    }
    setParatransit({
      ...paratransit,
      page,
      loading: true,
      initialLoad: false
    });
    const startDate = dayjs(selectedDates.start);
    const endDate = dayjs(selectedDates.end);
    const startString = startDate.add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss');
    const endString = endDate.add(4, 'hour').add(1, 'day').add(-1, 'second').format('YYYY-MM-DDTHH:mm:ss');
    api.get(`transdev/paratransitservice?page=${page}&size=${paratransit.size}&startDate=${startString}&endDate=${endString}&filter=${paratransit?.filter}`)
    .then((response) => {
      if (response.data.success) {
        setParatransit({
          ...paratransit,
          loading: false,
          totalCount: response.data.data.total,
          data: _.map(response.data.data.services, (s) => localizeParatransitServiceDates(s)),
          draftCount: response.data.data.draftCount,
          approvedCount: response.data.data.approvedCount,
          preApprovedCount: response.data.data.preApprovedCount,
          submittedCount: response.data.data.submittedCount,
          rejectedCount: response.data.data.rejectedCount
        });
      } else {
        setParatransit({
          ...paratransit,
          loading: false
        });
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: response.data.error,
            error: true,
          }
        ]);
      }
    })
    .catch((error) => {
      setParatransit({
        ...paratransit,
        loading: false
      });
      setNotifications([
        ...notifications,
        {
          title: t('error'),
          description: error.message,
          error: true,
        }
      ]);
    });
  }

  function getData() {
    if (selectedTab === 'special') {
      getSpecialData(0);
    } else {
      getParatransitData(0);
    }
  }

  useEffect(() => {
    setSelectedDates({
      start: dayjs().startOf('week').format('MMM DD, YYYY'),
      end: dayjs().endOf('week').format('MMM DD, YYYY')
    });
  }, []);

  useEffect(() => {
    if (selectedDates?.start && selectedDates?.end) {
      getData();
    }
  }, [selectedDates?.start, selectedDates?.end]);

  useEffect(() => {
    if (selectedTab === 'special' && special?.initialLoad) {
      getSpecialData(0);
    } else if (selectedTab === 'paratransit' && paratransit?.initialLoad) {
      getParatransitData(0);
    }
  }, [selectedTab]);

  useEffect(() => {
    if (selectedDates?.start && selectedDates?.end) {
      getData();
    }
  }, [special?.size, paratransit?.size, special?.filter, paratransit?.filter]);

  useEffect(() => {
    if (reloadSpecialServices) {
      setReloadSpecialServices(false);
      if (selectedTab === 'special') {
        getSpecialData(special.page);
      } else if (selectedTab === 'paratransit') {
        getParatransitData(paratransit.page);
      }
    }
  }, [reloadSpecialServices]);

  return (
    <MainPage>
      <H1 text={`Transdev - ${t('special_services')}`} />
      <div className="grid">
        <div className="flex float-right space-x-2">
          <RangePicker
            value={[
              dayjs(selectedDates?.start),
              dayjs(selectedDates?.end)
            ]}
            disabled={special.loading || paratransit.loading}
            className="h-11 mt-2 ml-auto bg-transparent"
            onChange={(obj) => {
              setSelectedDates({
                start: obj[0],
                end: obj[1]
              });
            }}
            format="MMM DD, YYYY"
          />
          <Button
            className="bg-primaryColor mb-4 px-2 mt-2"
            textColor="text-primaryTextColor"
            loaderColor="bg-primaryTextColor"
            borderColor="border-primaryBorderColor"
            text={t('refresh')}
            loading={special.loading || paratransit.loading}
            onClick={() => getData()}
          />
        </div>
        <Tabs activeKey={selectedTab} onChange={(key) => setSelectedTab(key)}>
          <Tabs.TabPane key="special" tab={t('special_services')}>
            <div className="grid grid-cols-1 md:grid-cols-6 mb-2">
              <Select
                className="col-span-1 md:col-span-2 md:col-start-5 lg:col-span-1 lg:col-start-6"
                disabled={special.loading}
                label={t('filter')}
                id="filter"
                name="filter"
                value={special.filter}
                onChange={(obj) => {
                  setSpecial({
                    ...special,
                    filter: obj.target.value
                  });
                }}
                options={[
                  { key: 0, value: 0, text: t('all') },
                  { key: 1, value: 1, text: t('draft') },
                  { key: 2, value: 2, text: t('submitted') },
                  { key: 4, value: 4, text: t('pre_approved') },
                  { key: 5, value: 5, text: t('approved') },
                  { key: 3, value: 3, text: t('rejected') },
                ]}
              />
            </div>
            <div className="gap-2 w-full grid grid-cols-1 md:grid-cols-6 lg:grid-cols-5">
              <Spin spinning={special?.loading}>
                <div className="border shadow-lg text-center p4 col-span-1 md:col-span-3 lg:col-span-1">
                  <p className="text-xl">{t('draft')}</p>
                  <p className="font-bold text-2xl">{thousand(special?.draftCount)}</p>
                </div>
              </Spin>
              <Spin spinning={special?.loading}>
                <div className="border shadow-lg text-center p4 col-span-1 md:col-span-3 lg:col-span-1">
                  <p className="text-xl">{t('submitted')}</p>
                  <p className="font-bold text-2xl">{thousand(special?.submittedCount)}</p>
                </div>
              </Spin>
              <Spin spinning={special?.loading}>
                <div className="border shadow-lg text-center p4 col-span-1 md:col-span-2 lg:col-span-1">
                  <p className="text-xl">{t('pre_approved')}</p>
                  <p className="font-bold text-2xl">{thousand(special?.preApprovedCount)}</p>
                </div>
              </Spin>
              <Spin spinning={special?.loading}>
                <div className="border shadow-lg text-center p4 col-span-1 md:col-span-2 lg:col-span-1">
                  <p className="text-xl">{t('approved')}</p>
                  <p className="font-bold text-2xl">{thousand(special?.approvedCount)}</p>
                </div>
              </Spin>
              <Spin spinning={special?.loading}>
                <div className="border shadow-lg text-center p4 col-span-1 md:col-span-2 lg:col-span-1">
                  <p className="text-xl">{t('rejected')}</p>
                  <p className="font-bold text-2xl">{thousand(special?.rejectedCount)}</p>
                </div>
              </Spin>
            </div>
            <Table
              bordered
              dataSource={special.data}
              loading={special.loading}
              rowKey="id"
              className="mt-2"
              locale={{
                emptyText: t('no_data')
              }}
              title={() => (
                <div className="flex">
                  <Button
                    className="bg-primaryColor px-2 w-full md:w-auto md:ml-auto"
                    textColor="text-primaryTextColor"
                    loaderColor="bg-primaryTextColor"
                    borderColor="border-primaryBorderColor"
                    text={t('create_new')}
                    disabled={special.loading || paratransit.loading}
                    onClick={() => setSelectedSpecialService(defaultSpecialService)}
                  />
                </div>
              )}
              pagination={{
                pageSize: special.size,
                showSizeChanger: true,
                current: special.page + 1,
                total: special?.totalCount,
                onChange: (page, size) => {
                  if (page - 1 !== special?.page) {
                    getSpecialData(page);
                  } else {
                    setSpecial({
                      ...special,
                      size
                    });
                  }
                }
              }}
              columns={[
                {
                  title: '',
                  align: 'center',
                  className: 'text-sm md:hidden',
                  render: (row) => (
                    <div className="grid grid-cols-2 gap-2">
                      <div className="text-left">
                        <p>{t('control_number')}</p>
                        <p className="font-bold">{row.controlNumber}</p>
                      </div>
                      <div className="text-right mt-6">
                        <Tag color={specialServiceStatusToColor(row.status)}>{t(specialServiceStatusToString(row.status))}</Tag>
                      </div>
                      <div className="text-left">
                        <p>{t('date')}</p>
                        <p className="font-bold">{dayjs(row.date).format('MMM DD, YYYY')}</p>
                      </div>
                      <div className="text-right">
                        <p>{t('time')}</p>
                        <p className="font-bold">{dayjs(row.date).format('hh:mm A')}</p>
                      </div>
                      <Button
                        className="bg-primaryColor col-span-2 mb-4 px-2 mt-2"
                        textColor="text-primaryTextColor"
                        loaderColor="bg-primaryTextColor"
                        borderColor="border-primaryBorderColor"
                        onClick={() => setSelectedSpecialService(row)}
                        text={t('view')}
                      />
                    </div>
                  ),
                },
                {
                  title: t('control_number'),
                  align: 'center',
                  className: 'text-sm hidden md:table-cell',
                  render: (row) => (
                    <span>{row.controlNumber}</span>
                  ),
                },
                {
                  title: t('date'),
                  align: 'center',
                  className: 'text-sm hidden md:table-cell',
                  render: (row) => (
                    <span>{dayjs(row.date).format('MMM DD, YYYY')}</span>
                  ),
                },
                {
                  title: t('time'),
                  align: 'center',
                  className: 'text-sm hidden md:table-cell',
                  render: (row) => (
                    <span>{dayjs(row.date).format('hh:mm A')}</span>
                  ),
                },
                {
                  title: t('status'),
                  align: 'center',
                  className: 'text-sm hidden md:table-cell',
                  render: (row) => (
                    <Tag color={specialServiceStatusToColor(row.status)}>{t(specialServiceStatusToString(row.status))}</Tag>
                  ),
                },
                {
                  title: t('actions'),
                  align: 'center',
                  className: 'text-sm hidden md:table-cell',
                  render: (row) => (
                    <Button
                      className="bg-primaryColor mb-4 px-2 mt-2"
                      textColor="text-primaryTextColor"
                      loaderColor="bg-primaryTextColor"
                      borderColor="border-primaryBorderColor"
                      onClick={() => setSelectedSpecialService(row)}
                      text={t('view')}
                    />
                  ),
                },
              ]}
            />
          </Tabs.TabPane>
          <Tabs.TabPane key="paratransit" tab={t('paratransit_services')}>
            <div className="grid grid-cols-1 md:grid-cols-6 mb-2">
              <Select
                className="col-span-1 md:col-span-2 md:col-start-5 lg:col-span-1 lg:col-start-6"
                disabled={special.loading}
                label={t('filter')}
                id="filter"
                name="filter"
                value={paratransit.filter}
                onChange={(obj) => {
                  setParatransit({
                    ...paratransit,
                    filter: obj.target.value
                  });
                }}
                options={[
                  { key: 0, value: 0, text: t('all') },
                  { key: 1, value: 1, text: t('draft') },
                  { key: 2, value: 2, text: t('submitted') },
                  { key: 4, value: 4, text: t('pre_approved') },
                  { key: 5, value: 5, text: t('approved') },
                  { key: 3, value: 3, text: t('rejected') },
                ]}
              />
            </div>
            <div className="gap-2 w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
              <Spin spinning={paratransit?.loading}>
                <div className="border shadow-lg text-center p4 col-span-1 md:col-span-2 lg:col-span-2">
                  <p className="text-xl">{t('draft')}</p>
                  <p className="font-bold text-2xl">{thousand(paratransit?.draftCount)}</p>
                </div>
              </Spin>
              <Spin spinning={paratransit?.loading}>
                <div className="border shadow-lg text-center p4 col-span-1 md:col-span-2 lg:col-span-2">
                  <p className="text-xl">{t('submitted')}</p>
                  <p className="font-bold text-2xl">{thousand(paratransit?.submittedCount)}</p>
                </div>
              </Spin>
              {/* <Spin spinning={paratransit?.loading}>
                <div className="border shadow-lg text-center p4 col-span-1 md:col-span-2 lg:col-span-1">
                  <p className="text-xl">{t('pre_approved')}</p>
                  <p className="font-bold text-2xl">{thousand(paratransit?.preApprovedCount)}</p>
                </div>
              </Spin> */}
              <Spin spinning={paratransit?.loading}>
                <div className="border shadow-lg text-center p4 col-span-1 md:col-span-2 lg:col-span-2">
                  <p className="text-xl">{t('approved')}</p>
                  <p className="font-bold text-2xl">{thousand(paratransit?.approvedCount)}</p>
                </div>
              </Spin>
              <Spin spinning={paratransit?.loading}>
                <div className="border shadow-lg text-center p4 col-span-1 md:col-span-2 lg:col-span-2">
                  <p className="text-xl">{t('rejected')}</p>
                  <p className="font-bold text-2xl">{thousand(paratransit?.rejectedCount)}</p>
                </div>
              </Spin>
            </div>
            <Table
              bordered
              dataSource={paratransit.data}
              loading={paratransit.loading}
              rowKey="id"
              className="mt-2"
              locale={{
                emptyText: t('no_data')
              }}
              title={() => (
                <div className="flex">
                  <Button
                    className="bg-primaryColor px-2 w-full md:w-auto md:ml-auto"
                    textColor="text-primaryTextColor"
                    loaderColor="bg-primaryTextColor"
                    borderColor="border-primaryBorderColor"
                    text={t('create_new')}
                    disabled={special.loading || paratransit.loading}
                    onClick={() => setSelectedParatransitService(defaultParatransitService)}
                  />
                </div>
              )}
              pagination={{
                pageSize: paratransit.size,
                showSizeChanger: true,
                current: paratransit.page + 1,
                total: paratransit?.totalCount,
                onChange: (page, size) => {
                  if (page - 1 !== paratransit?.page) {
                    getParatransitData(page);
                  } else {
                    setParatransit({
                      ...paratransit,
                      size
                    });
                  }
                }
              }}
              columns={[
                {
                  title: '',
                  align: 'center',
                  className: 'text-sm md:hidden',
                  render: (row) => (
                    <div className="grid grid-cols-2 gap-2">
                      <div className="text-right col-span-2">
                        <Tag color={paratransitServiceStatusToColor(row.status)}>{t(paratransitServiceStatusToString(row.status))}</Tag>
                      </div>
                      <div className="text-left">
                        <p>{t('control_number')}</p>
                        <p className="font-bold">{row.controlNumber}</p>
                      </div>
                      <div className="text-right">
                        <p>{t('date')}</p>
                        <p className="font-bold">{dayjs(row.date).format('MMM DD, YYYY')}</p>
                      </div>
                      <div className="text-left">
                        <p>{t('conductor')}</p>
                        <p className="font-bold">{row.driverName}</p>
                      </div>
                      <div className="text-right">
                        <p>{t('supervisor')}</p>
                        <p className="font-bold">{row.supervisorName}</p>
                      </div>
                      <div className="text-left">
                        <p>{t('unit_number')}</p>
                        <p className="font-bold">{row.unitNumber}</p>
                      </div>
                      <div className="text-right">
                        <p>{t('passengers')}</p>
                        <p className="font-bold">{thousand(row.passengers)}</p>
                      </div>
                      <Button
                        className="bg-primaryColor col-span-2 mb-4 px-2 mt-2"
                        textColor="text-primaryTextColor"
                        loaderColor="bg-primaryTextColor"
                        borderColor="border-primaryBorderColor"
                        onClick={() => setSelectedParatransitService(row)}
                        text={t('view')}
                      />
                    </div>
                  ),
                },
                {
                  title: t('control_number'),
                  align: 'center',
                  className: 'text-sm hidden md:table-cell',
                  render: (row) => (
                    <span>{row.controlNumber}</span>
                  ),
                },
                {
                  title: t('date'),
                  align: 'center',
                  className: 'text-sm hidden md:table-cell',
                  render: (row) => (
                    <span>{dayjs(row.date).format('MMM DD, YYYY')}</span>
                  ),
                },
                {
                  title: t('conductor'),
                  align: 'center',
                  className: 'text-sm hidden md:table-cell',
                  render: (row) => (
                    <span>{row.driverName}</span>
                  ),
                },
                {
                  title: t('supervisor'),
                  align: 'center',
                  className: 'text-sm hidden md:table-cell',
                  render: (row) => (
                    <span>{row.supervisorName}</span>
                  ),
                },
                {
                  title: t('unit_number'),
                  align: 'center',
                  className: 'text-sm hidden md:table-cell',
                  render: (row) => (
                    <span>{row.unitBusNumber}</span>
                  ),
                },
                {
                  title: t('passengers'),
                  align: 'center',
                  className: 'text-sm hidden md:table-cell',
                  render: (row) => (
                    <span>{thousand(row.passengers)}</span>
                  ),
                },
                {
                  title: t('status'),
                  align: 'center',
                  className: 'text-sm hidden md:table-cell',
                  render: (row) => (
                    <Tag color={paratransitServiceStatusToColor(row.status)}>{t(paratransitServiceStatusToString(row.status))}</Tag>
                  )
                },
                {
                  title: t('actions'),
                  align: 'center',
                  className: 'text-sm hidden md:table-cell',
                  render: (row) => (
                    <Button
                      className="bg-primaryColor mb-4 px-2 mt-2"
                      textColor="text-primaryTextColor"
                      loaderColor="bg-primaryTextColor"
                      borderColor="border-primaryBorderColor"
                      onClick={() => setSelectedParatransitService(row)}
                      text={t('view')}
                    />
                  ),
                },
              ]}
            />
          </Tabs.TabPane>
        </Tabs>
      </div>
      <SelectedSpecialServicesModal
        service={selectedSpecialService}
        onClose={() => setSelectedSpecialService(null)}
      />
      <SelectedParatransitServicesModal
        service={selectedParatransitService}
        onClose={() => setSelectedParatransitService(null)}
      />
    </MainPage>
  );
}

export default TransdevSpecialServices;
